import {
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Switch,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { QuoteApi } from "../../api/QuoteApi";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { RentalCalculation } from "../../lib/models/rental/rental-calculation.model";
import { RentalOverrideOptions } from "../../lib/models/rental/rental.model";
import { FixAndFlipCalculation } from "../../lib/models/short-term/short-term-calculation.model";
import { FixAndFlipForm } from "../../lib/models/short-term/short-term-form.model";
import { FixAndFlipOverrideOptions } from "../../lib/models/short-term/short-term.model";
import { toFixed } from "../../lib/number-transforms";
import { QuoteUtils } from "../../lib/quote-utils";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { isAutoExceptionUser, isElevatedUser } from "../../lib/UserPermissions";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import QuoteOwnerOverride from "../common-quote/QuoteOwnerOverride";
import AutoException from "./AutoException";
import FormLoadingOverlay from "./FormLoadingOverlay";
import ManualException from "./ManualException";
export type OverrideFormProps = {
  stfCalc?: FixAndFlipCalculation;
  rentalCalc?: RentalCalculation;
  exceptionPricingOverride: FixAndFlipOverrideOptions | RentalOverrideOptions;
  totalLoanAmt?: number;
  formHandler: (action: QuoteFormActions, values: FixAndFlipForm) => void;
};

function OverrideForm({
  stfCalc,
  rentalCalc,
  exceptionPricingOverride,
  totalLoanAmt,
  formHandler,
}: OverrideFormProps) {
  const quoteContext = useQuote();
  const { role } = useUser();
  const { state, quoteId } = quoteContext;
  const { isCalculating, metadata, quoteType } = state;
  const toast = useToast();
  const { setFieldValue, values } = useFormikContext<any>();
  const { rateReductionOverride, commissionReduction } = values;
  const [isLoading, setIsLoading] = useState(false);
  const toastVariant = useColorModeValue("subtle", "solid");

  const [isManualException, setIsManualException] = useState(
    values.arvOverride ||
      values.blendedLTCOverride ||
      values.finalOrigFeeOverride ||
      values.finalRateOverride ||
      values.purchaseLTCOverride ||
      values.processingFeeOverride ||
      values.achRequired !== "Yes" ||
      values.achExempt !== "Non-exempt" ||
      values.rateReductionOverride !== 0
  );

  const OverrideOptions = stfCalc
    ? FixAndFlipOverrideOptions
    : RentalOverrideOptions;

  const clearHandler = () => {
    const overrideArray = Object.keys(new OverrideOptions(null));
    overrideArray.forEach((option) => {
      if (option === "rateReductionOverride") {
        setFieldValue(option, "0.000");
      } else if (option === "autoRate") {
        setFieldValue(option, 0);
      } else if (option === "autoFee") {
        setFieldValue(option, 0);
      } else if (option === "achRequired") {
        setFieldValue(option, "Yes");
      } else if (option === "achExempt") {
        setFieldValue(option, "Non-exempt");
      } else if (option !== "exceptionType") {
        setFieldValue(option, "");
      }
    });
  };

  const submitExceptionHandler = useCallback(
    (status) => {
      if (!quoteId) return;

      setIsLoading(true);
      Promise.resolve(formHandler("SAVE", values))
        .then(() => {
          QuoteApi.submitQuoteExceptions(
            quoteId,
            status,
            !isManualException ? totalLoanAmt : undefined
          )
            .then((res) => {
              if (res.data) {
                toast({
                  title: "Exceptions Submitted",
                  description: "The exceptions have been submitted.",
                  status: "success",
                  variant: toastVariant,
                  isClosable: true,
                  position: "top-right",
                });
              }
            })
            .catch((err) => {
              console.log(err);
              toast({
                title: "Exceptions Submitted",
                description:
                  "An error has occurred when attempting to submit your exceptions.",
                status: "success",
                variant: toastVariant,
                isClosable: true,
                position: "top-right",
              });
            });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [
      quoteId,
      formHandler,
      values,
      toast,
      isManualException,
      totalLoanAmt,
      toastVariant,
    ]
  );

  const overrideInputs = useMemo(() => {
    const inputs = new OverrideOptions(values);
    return QuoteUtils.normalizeOverrideValues(inputs);
  }, [OverrideOptions, values]);

  const displayOverrides = useMemo(() => {
    for (const [key, value] of Object.entries(overrideInputs)) {
      switch (key) {
        case "achExempt":
          if (value && value !== "Non-exempt") {
            return true;
          }
          continue;
        case "achRequired":
          if (value && value !== "Yes") {
            return true;
          }
          continue;
        case "commissionReduction":
          if (value && value !== "0.000") {
            return true;
          }
          continue;
        default:
          if (value && key !== "exceptionType") {
            return true;
          } else {
            continue;
          }
      }
    }
    return false;
  }, [overrideInputs]);

  const maxRate = useMemo(() => {
    if (!rentalCalc?.scenarios) return;

    const max = Math.max(
      ...rentalCalc?.scenarios.map((scenario) => scenario.initFixedInterestRate)
    );
    return toFixed(max, 3);
  }, [rentalCalc?.scenarios]);

  const findScenarioPercentages = (baseRate: number) => {
    if (rateReductionOverride === undefined) {
      return baseRate;
    }

    const override = exceptionPricingOverride.rateReductionOverride ?? 0;

    if (+rateReductionOverride === 0) {
      return baseRate + +override;
    } else {
      return baseRate + +override - +rateReductionOverride;
    }
  };

  const disableInputs =
    metadata?.exceptionStatus === "Approved" ||
    metadata?.exceptionStatus === "Auto Approved";

  const disableCommission =
    metadata?.exceptionStatus !== "Not Submitted" && role !== "Capital Markets";

  const showCommission = isElevatedUser(role) || commissionReduction !== null;

  //To hide Auto Exceptions:
  //uncomment "allowAutoException = false", comment "allowAutoException = isAutoExceptionUser(metadata?.ownerRole)"
  //const allowAutoException = false;
  const allowAutoException = isAutoExceptionUser(metadata?.ownerRole);

  const onAutoExceptionToggle = () => {
    clearHandler();
    setIsManualException(!isManualException);
  };

  return (
    <Grid
      templateColumns="repeat(12, minmax(0, 1fr))"
      gap={{ base: 6, md: 8, lg: 8 }}
    >
      {isLoading && <FormLoadingOverlay label="Submitting..." />}
      <GridItem colSpan={12}>
        <Flex justifyContent="space-between" flexWrap="wrap">
          <FormSectionHeader
            id="override-section"
            label="Quote Override Options"
          />
          {displayOverrides && (
            <Button variant="outline" size="sm" onClick={clearHandler}>
              Clear Overrides
            </Button>
          )}
        </Flex>
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <QuoteOwnerOverride />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 12 }}>
        <hr />
      </GridItem>

      {allowAutoException && (
        <GridItem colSpan={12}>
          <HStack>
            <p>Manual Exception</p>
            <Switch
              isChecked={isManualException}
              onChange={onAutoExceptionToggle}
              isDisabled={disableInputs}
            />
          </HStack>
        </GridItem>
      )}

      {!isManualException && allowAutoException && (
        <AutoException
          handler={submitExceptionHandler}
          quoteType={quoteType}
          disableInputs={disableInputs && role !== "Operations Analyst"}
          loanAmount={totalLoanAmt}
          ownerBalance={metadata?.ownerBalance}
        />
      )}

      {(isManualException || !allowAutoException) && (
        <ManualException
          rentalCalc={rentalCalc}
          stfCalc={stfCalc}
          disableInputs={disableInputs && role !== "Operations Analyst"}
          disableCommission={disableCommission}
          rateReductionOverride={rateReductionOverride}
          showCommission={showCommission}
          isCalculating={isCalculating}
          findScenarioPercentages={findScenarioPercentages}
          maxRate={maxRate}
          handler={submitExceptionHandler}
        />
      )}
    </Grid>
  );
}

export default OverrideForm;
